/**
 * ═══════════════════════════════════════════════════════════════
 * Automated Error Monitoring — Global Error Tracker
 * ═══════════════════════════════════════════════════════════════
 *
 * Captures all unhandled errors, promise rejections, and network
 * failures. Logs to system_error_logs table and provides a
 * real-time error feed for the System Health dashboard.
 */
import { supabase } from '../supabase';
import { captureException, captureMessage } from './sentry';

interface ErrorEntry {
  module: string;
  page: string;
  error_type: string;
  error_message: string;
  stack?: string;
  user_role?: string;
  company_id?: string;
  timestamp: string;
}

// In-memory buffer — flushes to DB every 10 seconds (batching reduces DB writes)
const errorBuffer: ErrorEntry[] = [];
const MAX_BUFFER = 50;
let flushTimer: ReturnType<typeof setInterval> | null = null;

/** Flush buffered errors to Supabase */
async function flushErrors() {
  if (errorBuffer.length === 0) return;
  const batch = errorBuffer.splice(0, errorBuffer.length);
  try {
    await supabase.from('system_error_logs').insert(
      batch.map(e => ({
        module_name: e.module,
        page_url: e.page,
        action: e.error_type,
        error_message: e.error_message.slice(0, 2000),
        severity: 'error',
        environment: import.meta.env.MODE,
        company_id: e.company_id,
        created_at: e.timestamp,
      }))
    );
  } catch {
    // If DB insert fails, errors are lost — acceptable for monitoring
  }
}

/** Add an error to the buffer */
function trackError(entry: Omit<ErrorEntry, 'timestamp'>) {
  const full: ErrorEntry = { ...entry, timestamp: new Date().toISOString() };
  errorBuffer.push(full);

  // Also store in sessionStorage for the System Health page
  try {
    const stored = JSON.parse(sessionStorage.getItem('nizam_error_feed') ?? '[]');
    stored.push({ type: full.error_type, message: full.error_message.slice(0, 200), page: full.page, time: full.timestamp });
    if (stored.length > 100) stored.splice(0, stored.length - 100);
    sessionStorage.setItem('nizam_error_feed', JSON.stringify(stored));
  } catch {}

  // Parallel sink to Sentry (no-op when VITE_SENTRY_DSN is unset).
  // Real Error objects keep their stack; otherwise use a message capture.
  try {
    if (entry.error_type === 'unhandled_error' || entry.error_type === 'unhandled_rejection') {
      captureException(new Error(entry.error_message), {
        module: entry.module, page: entry.page, type: entry.error_type,
      });
    } else {
      captureMessage(entry.error_message, 'error', {
        module: entry.module, page: entry.page, type: entry.error_type,
      });
    }
  } catch { /* never let Sentry forwarding break the in-house tracker */ }

  // Immediate flush if buffer is full
  if (errorBuffer.length >= MAX_BUFFER) flushErrors();
}

/**
 * Initialize global error monitoring.
 * Call once in main.tsx before React renders.
 */
export function initErrorTracking() {
  // Start periodic flush
  if (!flushTimer) {
    flushTimer = setInterval(flushErrors, 10_000);
  }

  // Global JS errors
  window.addEventListener('error', (event) => {
    trackError({
      module: 'global',
      page: window.location.pathname,
      error_type: 'unhandled_error',
      error_message: event.message ?? 'Unknown error',
      stack: event.error?.stack,
    });
  });

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const message = event.reason instanceof Error
      ? event.reason.message
      : String(event.reason ?? 'Unhandled promise rejection');

    trackError({
      module: 'global',
      page: window.location.pathname,
      error_type: 'unhandled_rejection',
      error_message: message,
      stack: event.reason?.stack,
    });
  });

  // Network errors (fetch failures)
  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    try {
      const response = await originalFetch(...args);
      if (response.status >= 500) {
        const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request).url;
        trackError({
          module: 'network',
          page: window.location.pathname,
          error_type: 'server_error',
          error_message: `HTTP ${response.status} on ${url.split('?')[0]}`,
        });
      }
      return response;
    } catch (err) {
      trackError({
        module: 'network',
        page: window.location.pathname,
        error_type: 'network_failure',
        error_message: (err as Error).message ?? 'Network request failed',
      });
      throw err;
    }
  };

  // Flush on page unload
  window.addEventListener('beforeunload', () => flushErrors());
}

/** Get recent errors from sessionStorage (for dashboard display) */
export function getRecentErrors(): Array<{ type: string; message: string; page: string; time: string }> {
  try {
    return JSON.parse(sessionStorage.getItem('nizam_error_feed') ?? '[]');
  } catch {
    return [];
  }
}

/** Manual error tracking (call from catch blocks) */
export { trackError };
